import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { helper } from 'src/environments/helper';
import { BeneficiariService } from '../../../beneficiari.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { notDefaultValidator } from 'src/app/validators/not-default';
import { RelatieTypeService } from 'src/app/platform/nomenclatoare/activities-setari/relatie-type/relatie-type.service';
import { RelatieStatusService } from 'src/app/platform/nomenclatoare/activities-setari/relatie-status/relatie-status.service';
import { Beneficiar } from '../../../beneficiari.interface';
import { RelatieSpecificaService } from 'src/app/platform/nomenclatoare/activities-setari/relatie-specifica/relatie-specifica.service';
import { ConfirmareRelatieService } from 'src/app/platform/nomenclatoare/activities-setari/confirmare-relatie/confirmare-relatie.service';

@Component({
    selector: 'app-add-relationship',
    templateUrl: './add-relationship.component.html',
    styleUrls: ['./add-relationship.component.scss']
})
export class AddRelationshipComponent {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;

    form: FormGroup = new FormGroup({
        related_entity_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        relationship_type_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        relationship_specific_id: new FormControl(-1, []),
        relationship_status_id: new FormControl(-1, [Validators.required, notDefaultValidator('-1')]),
        relationship_confirmation_status_id: new FormControl(-1, []),
        observatii: new FormControl('', []),
    });

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    private destroy$ = new Subject<void>();

    beneficiari: (Generic & { cnp: string })[] = [];
    filteredBeneficiari: (Generic & { cnp: string })[] = [];
    beneficiariControl = new FormControl();

    relatieType: Generic[] = [];
    filteredRelatieType: Generic[] = [];
    relatieTypeControl = new FormControl();

    relatieSpecifica: Generic[] = [];
    filteredRelatieSpecifica: Generic[] = [];
    relatieSpecificaControl = new FormControl();

    relatieStatus: Generic[] = [];
    filteredRelatieStatus: Generic[] = [];
    relatieStatusControl = new FormControl();

    confirmareRelatie: Generic[] = [];
    filteredConfirmareRelatie: Generic[] = [];
    confirmareRelatieControl = new FormControl();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { beneficiar: Beneficiar },
        private dialogRef: MatDialogRef<AddRelationshipComponent>,
        private beneficiariService: BeneficiariService,
        private relatieTypeService: RelatieTypeService,
        private relatieSpecificaService: RelatieSpecificaService,
        private relatieStatusService: RelatieStatusService,
        private confirmareRelatieService: ConfirmareRelatieService,
        private notificationService: NotificationService,
    ) { }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit() {
        this.loadBeneficiari();
        this.loadRelatieType();
        this.loadRelatieSpecifica();
        this.loadRelatieStatus();
        this.loadConfirmareRelatie();

        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    renderFieldsSearch() {
        this.beneficiariControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredBeneficiari = this.beneficiari;
                    return of(this.beneficiari);
                }
                return this.filterBeneficiari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredBeneficiari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.relatieTypeControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredRelatieType = this.relatieType;
                    return of(this.relatieType);
                }
                return this.filterRelatieType(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredRelatieType = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.relatieSpecificaControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredRelatieSpecifica = this.relatieSpecifica;
                    return of(this.relatieSpecifica);
                }
                return this.filterRelatieSpecifica(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredRelatieSpecifica = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.relatieStatusControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredRelatieStatus = this.relatieStatus;
                    return of(this.relatieStatus);
                }
                return this.filterRelatieStatus(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredRelatieStatus = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.confirmareRelatieControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredConfirmareRelatie = this.confirmareRelatie;
                    return of(this.confirmareRelatie);
                }
                return this.filterConfirmareRelatie(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredConfirmareRelatie = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterBeneficiari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.beneficiari.filter(item =>
            this.normalizeString(item.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }
    filterRelatieType(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.relatieType.filter(item =>
            this.normalizeString(item.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }
    filterRelatieSpecifica(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.relatieSpecifica.filter(item =>
            this.normalizeString(item.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }
    filterRelatieStatus(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.relatieStatus.filter(item =>
            this.normalizeString(item.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }
    filterConfirmareRelatie(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.confirmareRelatie.filter(item =>
            this.normalizeString(item.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    loadBeneficiari() {
        this.isLoading = true;
        this.beneficiariService
            .getBeneficiari()
            .pipe(
                // Filter the active beneficiar
                map((results: any) => [...results.filter((item: Generic) => item.id !== this.data.beneficiar.id)]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.beneficiari = results;
                    this.filteredBeneficiari = [...this.beneficiari];
                },
                error: (error) => {
                    console.error('Error loading beneficiari', error);
                    this.filteredBeneficiari = [];
                },
            });
    }

    onBeneficiarClosed() {
        const selectedValue = this.beneficiariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredBeneficiari.some(item =>
                item.id === this.form.get('id_beneficiar')?.value
            );

            if (!isSelectedInFilteredList) {
                this.beneficiariControl.setValue(null);
                this.filteredBeneficiari = this.beneficiari;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadRelatieType() {
        this.isLoading = true;
        this.relatieTypeService
            .getListActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.relatieType = results;
                    this.filteredRelatieType = [...this.relatieType];
                },
                error: (error) => {
                    console.error('Error loading tipuri relatie', error);
                    this.filteredRelatieType = [];
                },
            });
    }

    onRelatieTypeClosed() {
        const selectedValue = this.relatieTypeControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredRelatieType.some(item =>
                item.id === this.form.get('id_relationship_type')?.value
            );

            if (!isSelectedInFilteredList) {
                this.relatieTypeControl.setValue(null);
                this.filteredRelatieType = this.relatieType;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadRelatieSpecifica() {
        this.isLoading = true;
        this.relatieSpecificaService
            .getListActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.relatieSpecifica = results;
                    this.filteredRelatieSpecifica = [...this.relatieSpecifica];
                },
                error: (error) => {
                    console.error('Error loading tipuri relatie', error);
                    this.filteredRelatieSpecifica = [];
                },
            });
    }

    onRelatieSpecificaClosed() {
        const selectedValue = this.relatieSpecificaControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredRelatieSpecifica.some(item =>
                item.id === this.form.get('id_relationship_specific')?.value
            );

            if (!isSelectedInFilteredList) {
                this.relatieSpecificaControl.setValue(null);
                this.filteredRelatieSpecifica = this.relatieSpecifica;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadRelatieStatus() {
        this.isLoading = true;
        this.relatieStatusService
            .getListActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.relatieStatus = results;
                    this.filteredRelatieStatus = [...this.relatieStatus];
                },
                error: (error) => {
                    console.error('Error loading statusuri relatie', error);
                    this.filteredRelatieStatus = [];
                },
            });
    }

    onRelatieStatusClosed() {
        const selectedValue = this.relatieStatusControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredRelatieStatus.some(item =>
                item.id === this.form.get('id_relationship_status')?.value
            );

            if (!isSelectedInFilteredList) {
                this.relatieStatusControl.setValue(null);
                this.filteredRelatieStatus = this.relatieStatus;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadConfirmareRelatie() {
        this.isLoading = true;
        this.confirmareRelatieService
            .getListActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results.data]),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results) => {
                    this.confirmareRelatie = results;
                    this.filteredConfirmareRelatie = [...this.confirmareRelatie];
                },
                error: (error) => {
                    console.error('Error loading statusuri relatie', error);
                    this.filteredConfirmareRelatie = [];
                },
            });
    }

    onConfirmareRelatieClosed() {
        const selectedValue = this.confirmareRelatieControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredConfirmareRelatie.some(item =>
                item.id === this.form.get('id_relationship_confirmation')?.value
            );

            if (!isSelectedInFilteredList) {
                this.confirmareRelatieControl.setValue(null);
                this.filteredConfirmareRelatie = this.confirmareRelatie;
                this.destroy$.next();
                this.destroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }


    submitForm() {
        this.isLoading = true;

        const payload = {
            ...this.form.value
        };

        // console.log('Payload add relationship: ', payload)
        this.beneficiariService
            .storeRelatie(this.data.beneficiar.id, payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
